import dayjs from 'dayjs'
import moment from 'jalali-moment'
import * as React from 'react'
import { Text, TouchableOpacity, View, ViewStyle } from 'react-native'

import { eventCellCss, u } from '../commonStyles'
import { ICalendarEvent } from '../interfaces'
import { useTheme } from '../theme/ThemeContext'
import {
  calculateWeightedColor,
  getTaskOfObject,
  isTaskBelongsDay,
  isToday,
  typedMemo,
} from '../utils'

export interface CalendarHeaderProps<T> {
  dateRange: dayjs.Dayjs[]
  cellHeight: number
  style: ViewStyle
  allDayEvents: ICalendarEvent<T>[]
  DayNumberContainerStyle: ViewStyle
  events: ICalendarEvent<T>[]
  onPressDateHeader?: (date: Date) => void
  onPressEvent?: (event: ICalendarEvent<T>) => void
  onPressAllDayEvents: any
}

function _CalendarHeader<T>({
  dateRange,
  cellHeight,
  style,
  allDayEvents,
  onPressDateHeader,
  DayNumberContainerStyle,
  events,
  onPressEvent = () => console.log('onPressEvent'),
  onPressAllDayEvents,
}: CalendarHeaderProps<T>) {
  const _onPress = React.useCallback(
    (date: Date) => {
      onPressDateHeader && onPressDateHeader(date)
    },
    [onPressDateHeader],
  )

  const theme = useTheme()
  const borderColor = { borderColor: theme.palette.gray['200'] }
  const primaryBg = { backgroundColor: theme.palette.primary.main }
  const isDayMode = dateRange.length == 1

  let dayTasks = React.useMemo(
    () =>
      dateRange.map((day) =>
        allDayEvents.filter((e) => {
          let eventObject = getTaskOfObject(e)
          return (
            eventObject?.type == 'Task' &&
            ((dayjs(e.start).isBetween(day.startOf('day'), day.endOf('day'), null, '[)') &&
              eventObject.isAllDay) ||
              !(
                (dayjs(e.start).isBefore(dayjs(day)) && dayjs(e.end).isBefore(dayjs(day))) ||
                (dayjs(e.start).isAfter(dayjs(day)) && dayjs(e.end).isAfter(dayjs(day)))
              ))
          )
        }),
      ),
    [dateRange, allDayEvents],
  )

  const todayColor = (date, isToDay) => {
    const todayEvents = events
      .filter(({ start, end }) => isTaskBelongsDay(start, end, date))
      .map((i) => {
        if (!(i && i.color)) i.color = '#FFFF00'
        return {
          color: i.color.substring(1, 7),
          duration:
            dayjs(i.end).diff(dayjs(i.start), 'hour', true) > 24
              ? 24
              : dayjs(i.end).diff(dayjs(i.start), 'hour', true),
        }
      })
    if (todayEvents.length == 0 && !isToDay) return 'transparent'
    if (todayEvents.length == 0 && isToDay) return '#2E2E2E'
    let weightedColor = calculateWeightedColor(todayEvents)
    return weightedColor
  }

  const allDayDoneColor = (allDayDoneEvenets) => {
    const todayEvents = allDayDoneEvenets.map((i) => {
      if (!(i && i.color)) i.color = '#FFFF00'
      return {
        color: i.color.substring(1, 7),
        duration: dayjs(i.end).diff(dayjs(i.start), 'hour', true),
      }
    })
    let weightedColor = calculateWeightedColor(todayEvents)
    return weightedColor
  }

  return (
    <View
      style={[
        u['border-b-2'],
        u['pt-2'],
        { backgroundColor: '#FAFAFA' },
        borderColor,
        theme.isRTL ? u['flex-row-reverse'] : u['flex-row'],
        style,
      ]}
    >
      {/* left side of header */}
      {isDayMode ? (
        <View style={[u['w-50'], u['items-center']]}>
          <Text style={[theme.typography.xs, u['mb-4']]}>
            {dayjs(dateRange[0]).format('ddd').toUpperCase()}
          </Text>
          <View
            style={[
              primaryBg,
              u['h-36'],
              u['w-36'],
              u['rounded-full'],
              u['items-center'],
              u['justify-center'],
              u['self-center'],
              u['z-20'],
              DayNumberContainerStyle,
              { backgroundColor: todayColor(dateRange[0], false) },
            ]}
          >
            <Text
              style={[
                theme.typography.sm,
                u['text-center'],
                { fontFamily: 'segoeui', lineHeight: 13 },
                //Platform.OS === 'web' && _isToday && u['mt-6'],
              ]}
            >
              {dateRange[0].format('D')}
            </Text>
            <Text
              style={[
                theme.typography.xs,
                u['text-center'],
                { fontFamily: 'segoeui', lineHeight: 12 },
                //Platform.OS === 'web' && _isToday && u['mt-6'],
              ]}
            >
              {moment(dateRange[0].format('YYYY-M-D'), 'YYYY-M-D HH:mm:ss')
                .locale('fa')
                .format('D')}
            </Text>
          </View>
          <Text style={[theme.typography.xs, u['mt-6'], u['mb-6']]}>All day</Text>
        </View>
      ) : (
        <View style={[u['w-50'], u['items-center']]}>
          <Text style={[theme.typography.xs, u['mb-4']]}>Week</Text>
          <View
            style={[u['w-36'], u['h-36'], u['bordered'], u['justify-center'], u['items-center']]}
          >
            <Text style={[theme.typography.xl]}>
              {Math.ceil(parseInt(dayjs(dateRange[0]).format('DD')) / 7)}
            </Text>
          </View>
          <Text style={[theme.typography.xs, u['mt-6']]}>All day</Text>
        </View>
      )}

      {/* day by day header */}
      {dateRange.map((date, index) => {
        const _isToday = isToday(date)
        const allDayDoneTaskEntities = dayTasks[index].filter((event) => {
          let eventObject = getTaskOfObject(event)
          return eventObject?.type == 'Task' && eventObject?.activity?.status == 'Done'
        })
        // console.log('allDayTasks for day', date, 'is :', dayTasks[index].length)
        return (
          <TouchableOpacity
            style={[u['flex-1'], u['pt-2'], u['mb-2']]}
            onPress={() => _onPress(date.toDate())}
            disabled={onPressDateHeader === undefined}
            key={date.toString()}
          >

            {/* View containing 2 Miladi and jallai day numbers */}
            {!isDayMode && (
              <View style={[u['justify-between'], { minHeight: cellHeight }]}>
                <Text
                  style={[
                    theme.typography.xs,
                    u['text-center'],
                    u['mb-4'],
                    {
                      color: todayColor(date, true),
                      fontFamily: 'segoeui',
                      fontSize: 11,
                    },
                  ]}
                >
                  {date.format('ddd')[0]}
                </Text>
                <View
                  style={
                    !_isToday
                      ? [
                          primaryBg,
                          u['h-36'],
                          u['w-36'],
                          u['rounded-full'],
                          u['items-center'],
                          u['justify-center'],
                          u['self-center'],
                          u['z-20'],
                          DayNumberContainerStyle,
                          { backgroundColor: todayColor(date, false) },
                        ]
                      : [
                          primaryBg,
                          u['h-36'],
                          u['w-36'],
                          u['pb-2'],
                          u['rounded-full'],
                          u['items-center'],
                          u['justify-center'],
                          u['self-center'],
                          u['z-20'],
                          {
                            borderWidth: 2.5,
                            borderColor: todayColor(date, false),
                            backgroundColor: 'transparent',
                          },
                        ]
                  }
                >
                  <Text
                    style={[
                      theme.typography.sm,
                      u['text-center'],
                      { fontFamily: 'segoeui', lineHeight: 13 },
                      //Platform.OS === 'web' && _isToday && u['mt-6'],
                    ]}
                  >
                    {date.format('D')}
                  </Text>
                  <Text
                    style={[
                      theme.typography.xs,
                      u['text-center'],
                      { fontFamily: 'segoeui', lineHeight: 12 },
                      //Platform.OS === 'web' && _isToday && u['mt-6'],
                    ]}
                  >
                    {moment(date.format('YYYY-M-D'), 'YYYY-M-D HH:mm:ss').locale('fa').format('D')}
                  </Text>
                </View>
              </View>
            )}

            {/* view containing allDay events and tasks */}
            <View
              style={[{ borderColor: theme.palette.gray['200'] }, u['mt-2'], { minHeight: 20 }]}
            >
              {/* box containing event cases */}
              {dayTasks[index].length > 0 && (
                <TouchableOpacity
                  style={[
                    eventCellCss.style,
                    { backgroundColor: allDayDoneColor(dayTasks[index]) },
                    isDayMode && { height: 20, paddingLeft: 6, justifyContent: 'center' },
                    { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' },
                  ]}
                  onPress={() => onPressAllDayEvents(date)}
                >
                  {/* <Image source={require('../../assets/images/Done.png')} style={{height:10, width: 10, marginHorizontal: 2}} /> */}
                  {allDayDoneTaskEntities.length == dayTasks[index].length ? (
                    <>
                      <Text
                        style={[
                          {
                            fontSize: 8,
                            color: theme.palette.primary.contrastText,
                            fontFamily: 'segoeui',
                          },
                          isDayMode && { fontSize: 11 },
                        ]}
                      >
                        {allDayDoneTaskEntities.length} Done
                      </Text>
                    </>
                  ) : (
                    <Text
                      style={[
                        {
                          fontSize: 8,
                          color: theme.palette.primary.contrastText,
                          fontFamily: 'segoeui',
                        },
                        isDayMode && { fontSize: 11 },
                      ]}
                    >
                      {allDayDoneTaskEntities.length} of {dayTasks[index].length}
                    </Text>
                  )}
                </TouchableOpacity>
              )}

              {/* show just events seperately */}
              {allDayEvents.map((event) => {
                // dont show if is a task
                let eventObject = getTaskOfObject(event)

                if (!dayjs(event.start).isSame(date, 'day') || eventObject?.type == 'Task') {
                  return null
                }

                // show if is a event
                return (
                  <TouchableOpacity
                    style={[
                      eventCellCss.style,
                      { backgroundColor: event.color },
                      // { width: getWidthOfAllDayInPercent(event.start, event.end) + '%' },
                      isDayMode && { height: 20, paddingLeft: 6, justifyContent: 'center' },
                    ]}
                    key={`${event.start}${event.title}`}
                    onPress={() => onPressEvent(event)}
                  >
                    <Text
                      style={[
                        {
                          fontSize: 8,
                          color: theme.palette.primary.contrastText,
                          fontFamily: 'segoeui',
                        },
                        isDayMode && { fontSize: 11 },
                      ]}
                    >
                      {event.title}
                    </Text>
                  </TouchableOpacity>
                )
              })}
            </View>
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

export const CalendarHeader = typedMemo(_CalendarHeader)
